import { create } from "zustand";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: number;
  message: string;
  kind: "info" | "success" | "error";
  action?: ToastAction;
  /** ms; 0 = sticky until dismissed */
  duration: number;
}

interface ToastStore {
  toasts: Toast[];
  push: (t: Omit<Toast, "id" | "duration" | "kind"> & Partial<Pick<Toast, "duration" | "kind">>) => number;
  dismiss: (id: number) => void;
}

let nextId = 1;

export const useToasts = create<ToastStore>((set) => ({
  toasts: [],
  push: (t) => {
    const id = nextId++;
    const toast: Toast = { kind: "info", duration: 4000, ...t, id };
    set((s) => ({ toasts: [...s.toasts, toast] }));
    if (toast.duration > 0) {
      setTimeout(() => {
        set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) }));
      }, toast.duration);
    }
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) })),
}));

/** Imperative helper usable outside React. */
export const toast = {
  info: (message: string, action?: ToastAction) => useToasts.getState().push({ message, action }),
  success: (message: string, action?: ToastAction) => useToasts.getState().push({ message, action, kind: "success" }),
  error: (message: string, action?: ToastAction) => useToasts.getState().push({ message, action, kind: "error", duration: 8000 }),
  sticky: (message: string, action?: ToastAction) => useToasts.getState().push({ message, action, duration: 0 }),
};
